// Spells that show they are at work for as long as they are active:
//   - a screen: a see-through sheet of light laid over its caster's side of the mat (Light Screen), with a soft
//     glowing edge and a slow sheen drifting across it
//   - a dome: a see-through dome over the Spell's own card, like the one a Buffer puts over a Unit (Arcane Dome)
// (CONFIG.looks.sideScreen and CONFIG.looks.cardDome say which Spells, and their colors.)
import * as THREE from 'three';
import { TokenFx } from './tokenFx.ts';

export interface ScreenWant {
  key: string;
  side: 1 | -1; // which half of the mat, as seen on this table (1 = the near half)
  colors: string[]; // [sheet, edge]
}
export interface DomeWant {
  key: string;
  card: THREE.Object3D; // the Spell's card on the table
  color: string;
}

let edgeTex: THREE.Texture | null = null;
/** A sheet that is clear in the middle and glows toward its edges. */
function screenTexture() {
  if (edgeTex) return edgeTex;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 128;
  const g = c.getContext('2d')!;
  g.fillStyle = 'rgba(255,255,255,0.35)';
  g.fillRect(0, 0, 256, 128);
  for (let i = 0; i < 14; i++) {
    const a = (1 - i / 14) * 0.09;
    g.strokeStyle = `rgba(255,255,255,${a * 6})`;
    g.lineWidth = 2;
    g.strokeRect(i, i, 256 - i * 2, 128 - i * 2);
  }
  edgeTex = new THREE.CanvasTexture(c);
  return edgeTex;
}
let sheenTex: THREE.Texture | null = null;
/** A soft diagonal band of light. */
function bandTexture() {
  if (sheenTex) return sheenTex;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 16;
  const g = c.getContext('2d')!;
  const grad = g.createLinearGradient(0, 0, 256, 0);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.5, 'rgba(255,255,255,1)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 16);
  sheenTex = new THREE.CanvasTexture(c);
  return sheenTex;
}

interface Screen {
  group: THREE.Group;
  sheet: THREE.Mesh;
  band: THREE.Mesh;
  born: number;
  w: number;
}

export class SpellScenes {
  private scene: THREE.Scene;
  private screens = new Map<string, Screen>();
  private domes = new Map<string, TokenFx>();
  private domeCards = new Map<string, THREE.Object3D>();
  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** The mat's size, to lay a screen over one half of it. */
  sync(screens: ScreenWant[], domes: DomeWant[], mat: { width: number; depth: number }) {
    const wantS = new Set(screens.map((w) => w.key));
    for (const [k, sc] of this.screens) {
      if (wantS.has(k)) continue;
      this.scene.remove(sc.group);
      for (const m of [sc.sheet, sc.band]) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
      this.screens.delete(k);
    }
    for (const w of screens) {
      if (this.screens.has(w.key)) continue;
      const group = new THREE.Group();
      const width = mat.width * 0.98;
      const depth = (mat.depth / 2) * 0.96;
      const geo = new THREE.PlaneGeometry(width, depth);
      geo.rotateX(-Math.PI / 2);
      const sheet = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: screenTexture(), color: w.colors[0], transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      sheet.position.y = 0.12;
      sheet.renderOrder = 4;
      const bandGeo = new THREE.PlaneGeometry(width * 0.35, depth);
      bandGeo.rotateX(-Math.PI / 2);
      const band = new THREE.Mesh(bandGeo, new THREE.MeshBasicMaterial({ map: bandTexture(), color: w.colors[1] ?? w.colors[0], transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      band.position.y = 0.14;
      band.renderOrder = 4;
      group.add(sheet, band);
      group.position.z = (w.side * mat.depth) / 4;
      this.scene.add(group);
      this.screens.set(w.key, { group, sheet, band, born: performance.now() / 1000, w: width });
    }
    const wantD = new Set(domes.map((d) => d.key));
    for (const [k, fx] of this.domes) {
      if (wantD.has(k)) continue;
      fx.dispose();
      this.domes.delete(k);
      this.domeCards.delete(k);
    }
    for (const d of domes) {
      if (!this.domes.has(d.key)) this.domes.set(d.key, new TokenFx({ color: d.color, parts: [{ t: 'dome', color: d.color, min: 0.42, max: 0.42 }] } as never, 1, new THREE.Object3D(), 0));
      this.domeCards.set(d.key, d.card);
    }
  }

  update(time: number) {
    for (const sc of this.screens.values()) {
      const grow = Math.min(1, (time - sc.born) / 0.8); // (it spreads over the half as it is cast)
      const breath = 0.85 + 0.15 * Math.sin(time * 1.3);
      (sc.sheet.material as THREE.MeshBasicMaterial).opacity = 0.3 * grow * breath;
      sc.group.scale.set(0.3 + 0.7 * grow, 1, 0.3 + 0.7 * grow);
      const u = ((time * 0.12) % 1.4) - 0.2; // a sheen drifting across, now and then
      sc.band.position.x = (u - 0.5) * sc.w;
      (sc.band.material as THREE.MeshBasicMaterial).opacity = grow * 0.28 * Math.max(0, Math.sin(Math.PI * Math.min(1, Math.max(0, u))));
    }
    for (const [k, fx] of this.domes) fx.update({ time, value: 1, k: 1, card: this.domeCards.get(k) ?? null });
  }

  dispose() {
    this.sync([], [], { width: 1, depth: 1 });
  }
}
